import {
  InvalidInput,
  NumberField,
  Popover,
  RangeField,
  readLink,
  type RangeInput,
  type RangeValue,
} from '@trippath/shared';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { linkOf, timeOf } from '../../lib/clock';
import { problemKey } from '../../lib/format';

/** The ranges a cost edit changes, by what they are. */
export type CostKey = 'accommodation' | 'food' | 'local' | 'activities' | 'estimate';

/** What a cost edit changes: some ranges by name, the days, a way's times. */
export interface CostValues {
  ranges: Partial<Record<CostKey, RangeInput>>;
  /** The days typed; 0 when there was no field for them. */
  days: number;
  /** The times typed; none when there was no field for them. */
  times: { departure: string | null; arrival: string | null };
  /** The times as kept, once a way's clock has read them: instants in UTC. */
  kept?: { departureAt: string | null; arrivalAt: string | null };
  /** The ticket in mind, typed; none when empty or there was no field for it. */
  name: string | null;
  /** The link typed to open it again; none when empty or there was no field for it. */
  link: string | null;
}

/** A range to change: one price or from one to another; one that may be left out is folded. */
export interface CostRange {
  key: CostKey;
  label: string;
  value: RangeInput;
  /** Not always wanted, as activities for a day before leaving: opened by a button. */
  optional?: boolean;
}

interface Props {
  /** The trigger's name, said by a screen reader: what is changed. */
  label: string;
  /** What the trigger shows: the price as it is. */
  children: ReactNode;
  currency: string;
  ranges: CostRange[];
  days?: number;
  /** When a way leaves and arrives, and about how long it takes, to say when it arrives. */
  times?: {
    departure: string | null;
    arrival: string | null;
    /** Its arrival, about, from its departure; none when that cannot be told. */
    about: (departure: string) => string | null;
  };
  /** The ticket in mind. */
  name?: { label: string; value: string | null };
  /** The link it was found at, to open again. */
  link?: string | null;
  onSave: (values: CostValues) => Promise<unknown>;
}

/**
 * A price shown that opens, when pressed, what it is made of — each one price or a range —
 * the days, a way's times and the ticket in mind with its link, saved together.
 */
export function CostEdit({
  label,
  children,
  currency,
  ranges,
  days,
  times,
  name,
  link,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const ids = useId();
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<
    { key: CostKey; label: string; value: RangeValue; shown: boolean }[]
  >([]);
  const [count, setCount] = useState<number | null>(null);
  const [leaves, setLeaves] = useState('');
  const [arrives, setArrives] = useState('');
  const [called, setCalled] = useState('');
  const [address, setAddress] = useState('');
  // What the link said when it was last read, and which link that was.
  const [found, setFound] = useState<{ link: string; name: string | null } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const start = (next: boolean) => {
    if (next) {
      // One that may be left out shows when it has something in it.
      setFields(
        ranges.map((r) => ({
          key: r.key,
          label: r.label,
          value: r.value,
          shown: r.optional !== true || r.value.max > 0,
        })),
      );
      setCount(days ?? null);
      setLeaves(times?.departure ?? '');
      setArrives(times?.arrival ?? '');
      setCalled(name?.value ?? '');
      setAddress(link ?? '');
      setFound(null);
      setFailed(null);
    }
    setOpen(next);
  };
  const leftAt = timeOf(leaves);
  const arrivedAt = timeOf(arrives);
  const linked = linkOf(address);
  // When it arrives, about, from when it leaves and how long it takes.
  const estimate = times && typeof leftAt === 'string' ? times.about(leftAt) : null;
  const about = typeof estimate === 'string' ? `~${estimate}` : '';

  const save = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const read: CostValues['ranges'] = {};
    for (const { key, value } of fields) {
      if (value.min === null || value.max === null || value.min > value.max) {
        setFailed(t('problem.reversed'));
        return;
      }
      read[key] = { min: value.min, max: value.max };
    }
    if (
      leftAt === undefined ||
      arrivedAt === undefined ||
      linked === undefined ||
      (days !== undefined && count === null)
    ) {
      setFailed(t('problem.format'));
      return;
    }
    setFailed(null);
    onSave({
      ranges: read,
      days: count ?? 0,
      times: { departure: leftAt, arrival: arrivedAt },
      name: called.trim() === '' ? null : called.trim(),
      link: linked,
    }).then(
      () => {
        setOpen(false);
      },
      (error: unknown) => {
        const [first] = error instanceof InvalidInput ? error.fields : [];
        setFailed(t(first ? problemKey(first.problem) : 'plan.failed'));
      },
    );
  };
  // A link typed is read once it is left: what it is, named where nothing is named yet.
  const look = () => {
    if (typeof linked !== 'string' || found?.link === linked) return;
    readLink(linked).then(
      (read) => {
        setFound({ link: linked, name: read.name });
        if (read.name !== null && called.trim() === '') setCalled(read.name);
      },
      () => {
        setFound({ link: linked, name: null });
      },
    );
  };
  const field = (
    id: string,
    caption: string,
    value: string,
    set: (value: string) => void,
    extra: { placeholder?: string; invalid?: boolean; numeric?: boolean } = {},
  ) => (
    <div className="field">
      <label className="field-label" htmlFor={`${ids}-${id}`}>
        {caption}
      </label>
      <input
        id={`${ids}-${id}`}
        className="text-input"
        type="text"
        inputMode={extra.numeric === true ? 'numeric' : 'text'}
        autoComplete="off"
        placeholder={extra.placeholder}
        value={value}
        aria-invalid={extra.invalid === true}
        onChange={(event) => {
          set(event.target.value);
        }}
      />
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={start}
      label={label}
      trigger={(props) => (
        <button {...props} type="button" className="cost-edit" aria-label={label}>
          {children}
        </button>
      )}
    >
      <form className="cost-form" onSubmit={save} noValidate>
        {name && field('name', name.label, called, setCalled)}
        {days !== undefined && (
          <NumberField
            label={t('stopForm.days')}
            value={count}
            onChange={setCount}
            min={0}
            max={365}
          />
        )}
        {fields.map((range) =>
          range.shown ? (
            <RangeField
              key={range.key}
              label={range.label}
              value={range.value}
              currency={currency}
              onChange={(value) => {
                setFields((was) => was.map((f) => (f.key === range.key ? { ...f, value } : f)));
              }}
            />
          ) : (
            <button
              key={range.key}
              type="button"
              className="link-button cost-more"
              onClick={() => {
                setFields((was) =>
                  was.map((f) => (f.key === range.key ? { ...f, shown: true } : f)),
                );
              }}
            >
              + {range.label}
            </button>
          ),
        )}
        {times && (
          <div className="cost-times">
            {field('leaves', t('plan.leaves'), leaves, setLeaves, {
              placeholder: '08:15',
              invalid: leftAt === undefined,
              numeric: true,
            })}
            {field('arrives', t('plan.arrives'), arrives, setArrives, {
              placeholder: about,
              invalid: arrivedAt === undefined,
              numeric: true,
            })}
          </div>
        )}
        {link !== undefined && (
          <div className="field">
            <label className="field-label" htmlFor={`${ids}-link`}>
              {t('plan.link')}
            </label>
            <div className="stay-hotel-link">
              <input
                id={`${ids}-link`}
                className="text-input"
                type="text"
                autoComplete="off"
                placeholder="https://…"
                value={address}
                aria-invalid={linked === undefined}
                onChange={(event) => {
                  setAddress(event.target.value);
                }}
                onBlur={look}
              />
              {typeof linked === 'string' && (
                <a className="text-link" href={linked} target="_blank" rel="noreferrer">
                  {t('plan.openLink')}
                </a>
              )}
            </div>
            {found?.name && (
              <p className="quiet" role="status">
                {t('plan.found', { name: found.name })}
              </p>
            )}
          </div>
        )}
        {failed !== null && (
          <p className="field-error" role="alert">
            {failed}
          </p>
        )}
        <div className="dialog-actions">
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-small button-primary">
            {t('car.save')}
          </button>
        </div>
      </form>
    </Popover>
  );
}
